import { Skeleton } from 'antd'
import classNames from 'classnames'
import { useAppSelector } from 'common/hooks/redux'
import {
  useFetchEncounterLabsQuery,
  useFetchPhotoStreamQuery,
} from 'features/Procedure/state/procedure.api'
import { useEffect, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { useParams } from 'react-router'
import styles from './preview.module.scss'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

type Props = {
  documentId?: number
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  scrollMode?: 'self' | 'parent'
}

export const LabsPreview = ({
  documentId,
  selectedPatientId,
  selectedEncounterUid,
  scrollMode = 'self',
}: Props) => {
  const { siteId } = useParams<{ siteId: string }>()
  const selectedLabId = useAppSelector((state) => state.procedureReducer.selectedLabId)

  const currentDocumentId = documentId || selectedLabId
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)

  const shouldSkipLabsQuery = !siteId || !selectedPatientId || !selectedEncounterUid

  const { isFetching: isFetchingLabs, isLoading: isLoadingLabs } = useFetchEncounterLabsQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      encounterUid: selectedEncounterUid!,
    },
    {
      skip: shouldSkipLabsQuery,
    },
  )

  const { data: pdfBlob, isFetching: isFetchingPdf } = useFetchPhotoStreamQuery(
    {
      siteId: siteId!,
      patientId: selectedPatientId!,
      documentId: currentDocumentId!,
    },
    {
      skip: !currentDocumentId || !siteId || !selectedPatientId,
    },
  )

  useEffect(() => {
    if (pdfBlob && pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob)
      setPdfUrl(url)
      return () => {
        URL.revokeObjectURL(url)
      }
    } else {
      setPdfUrl(null)
    }
  }, [pdfBlob])

  useEffect(() => {
    if (!currentDocumentId) {
      setPdfUrl(null)
      setNumPages(null)
    }
  }, [currentDocumentId])

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }

  const onDocumentLoadError = (error: Error) => {
    console.error('Error loading PDF:', error)
  }

  // Show skeleton when labs are fetching or PDF is fetching
  if (isFetchingLabs || isLoadingLabs || isFetchingPdf) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 12 }} />
      </div>
    )
  }

  // Show empty state only when no document is selected
  if (!currentDocumentId) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span>No document selected</span>
        </div>
      </div>
    )
  }

  if (!pdfUrl) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>Failed to load document</div>
      </div>
    )
  }

  return (
    <Document
      file={pdfUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      onLoadError={onDocumentLoadError}
      loading={<Skeleton active paragraph={{ rows: 12 }} />}
      className={classNames(styles.document, {
        [styles.documentParentScroll]: scrollMode === 'parent',
      })}>
      <div className={styles.pageWrapper}>
        {Array.from(new Array(numPages), (_, index) => (
          <Page
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            className={styles.page}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            width={undefined}
          />
        ))}
      </div>
    </Document>
  )
}
